import React from 'react';
import PropTypes from 'prop-types';
import TaxonomyResults from './TaxonomyResults';
import CompanyResults from './CompanyResults';
import { makeStyles } from '@material-ui/core';
import groupBy from 'lodash/groupBy';
import DJMLogo from 'src/assets/images/DJM-logo.svg';
/**
 * @typedef {import('src/lib/type').SearchHit} SearchHit
 * @typedef {import('src/lib/type').CompanySearchHit} CompanySearchHit
 * @typedef {import('src/lib/type').CategorySearchHit} CategorySearchHit
 * @typedef {import('src/lib/type').SubcategorySearchHit} SubcategorySearchHit
 */

const useStyles = makeStyles((theme) => ({
  resultsLayout: {
    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '25rem 1fr',
    },
  },
  footer: {
    padding: '8px 16px',
    backgroundColor: 'rgba(249, 250, 251, 0.5)',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#6b7280',
  },
  errorContainer: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '14px',
  },
  poweredBy: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  djmLogo: {
    marginBottom: '-4px',
    width: '80px',
  },
}));

/**
 * Container component for displaying search results
 * @param {Object} props - The component properties
 * @param {SearchHit[]} props.suggestions - The suggestions to display
 * @param {boolean} props.isError - Indicates if there is an error
 * @param {function} props.onItemClick - Function called when clicking on an element
 * @returns {JSX.Element} The search results component
 */
export default function SearchResults({ suggestions, isError, onItemClick }) {
  const classes = useStyles();
  // Separate results by type
  const groupedResults = groupBy(suggestions, (hit) => hit.type === "company" ? "company" : "taxonomy");

  /** @type {CompanySearchHit[]} */
  const companyResults = groupedResults['company'] || [];

  /** @type {(CategorySearchHit | SubcategorySearchHit)[]} */
  const taxonomyResults = groupedResults['taxonomy'] || [];

  if (isError) {
    return (
      <div className={classes.errorContainer}>
        Error loading search results. Please try again.
      </div>
    );
  }

  return (
    <>
      <div className={classes.resultsLayout}>
        {/* Taxonomy Section */}
        <TaxonomyResults
          suggestions={taxonomyResults}
          onItemClick={onItemClick}
        />

        {/* Companies Section */}

        <CompanyResults
          suggestions={companyResults}
          onItemClick={onItemClick}
        />
      </div>

      {/* Footer */}

      <div className={classes.footer}>
        <span className={classes.poweredBy}>
          Powered by{' '}
          <a
            href="https://djmdigital.be/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={DJMLogo} alt="DJM Logo" className={classes.djmLogo} />
          </a>
        </span>
        <span>
          {suggestions.length} result{suggestions.length !== 1 ? 's' : ''} found
        </span>
      </div>
    </>
  );
}

SearchResults.propTypes = {
  /** The suggestions to display */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['category', 'subcategory', 'company']).isRequired,
      title: PropTypes.string.isRequired,
      searchField: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ),
  /** Indicates if there is an error */
  isError: PropTypes.bool,
  /** Function called when clicking on an element */
  onItemClick: PropTypes.func,
};

SearchResults.defaultProps = {
  suggestions: [],
  isError: false,
  onItemClick: () => {},
};
